import React, { useEffect, useState } from 'react'
import {
  IonCol,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import { AccordianComponent } from '../../../../components/accordian/Accordian.components'
import { useTranslation } from 'react-i18next'
import PulsesContent from '../pulses/content/pulses.content'
import { getPulsesSelector } from '../pulses/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { getInitialAssessmentCardioOptionsSelector } from '../../../options/selectors/getInitialAssessmentCardioOptions.selector'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { getInitialAssessmentCardio } from '../selectors/getInitialAssessmentCardio.selectors'
import { IOption } from '../../../../common/common.types'
import {
  saveCardio,
  setCvArt,
  setCvCapRefill,
  setCvEdema,
  setCvJvd,
  setCvSwan,
  setCvTemp,
  setCvTempDegrees,
  setCvTempMethod,
  setMa,
  setPmMode,
  setPrCvAssess,
  setRate,
} from '../store/cardio.actions'
import MultiSelect from '../../../../components/multiSelect/MultiSelect.component'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import Comment from '../../../../components/comment/Comment.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { IConfigurationsState } from '../../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../../configurations/selectors'
import './Cardio.content.css'
import { getTripInformationSelector } from '../../../tripInformation/selectors'
import PacemakerContent from '../pacemaker/content/pacemaker.content'
import Validation from '../../../../components/validation/Validation.component'
import { CardioConstants } from '../store/cardio.constants'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'

const CardioContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isTablet } = useDeviceMetrics()
  const cardioOptions = useSelector(getInitialAssessmentCardioOptionsSelector)
  const {
    cvArtOptions,
    cvSwanOptions,
    cvTempMethodOptions,
    cvJvdOptions,
    cvCapRefillOptions,
    cvEdemaOptions,
    prCvAssessOptions,
  } = cardioOptions

  const cardioState = useSelector(getInitialAssessmentCardio)
  const { prPm, prNabc, prCvAssess } = cardioState
  const { type, rate, ma, pmMode } = prPm
  const { cvArt, cvSwan, cvTemp, cvTempDegrees, cvTempMethod, cvJvd, cvCapRefill, cvEdema, cvComments } = prNabc
  const configuration: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { datapointsConfig } = configuration
  const { staffingLevel } = useSelector(getTripInformationSelector)
  const showTemp = datapointsConfig.DP_TEMP === 1 ? true : false
  const showEdema = datapointsConfig.DP_EDEMA === 1 ? true : false
  const showPaLine = datapointsConfig.DP_SWAN === 1 ? true : false
  const showArtLine = datapointsConfig.DP_ARTLINE === 1 ? true : false
  const showInvasiveMonitoring = staffingLevel?.id === 4 ? true : false
  const showPacemaker = staffingLevel?.isAls === 1
  const pulses = useSelector(getPulsesSelector)
  const {
    pulseBraL,
    pulseBraR,
    pulseCarotid,
    pulseCarotidR,
    pulseDorsL,
    pulseDorsR,
    pulseFemL,
    pulseFemR,
    pulseRadL,
    pulseRadR,
  } = pulses
  const showPulses = Boolean(
    pulseBraL ||
      pulseBraR ||
      pulseCarotid ||
      pulseCarotidR ||
      pulseDorsL ||
      pulseDorsR ||
      pulseFemL ||
      pulseFemR ||
      pulseRadL ||
      pulseRadR
  )
  const [cardioValidationFlag, setCardioValidationFlag] = useState(true)

  const icPaceMakerCheck = Boolean(type || rate || ma || pmMode)

  const invasiveMonitoringCheck = Boolean(cvArt.length || cvSwan)

  const setPaLineOnChange = (option: IOption) => {
    dispatch(setCvSwan(option))
    dispatch(saveCardio())
  }
  const setTempMethodOnChange = (option: IOption) => {
    dispatch(setCvTempMethod(option))
    dispatch(saveCardio())
  }
  const setJVDOnChange = (option: IOption) => {
    dispatch(setCvJvd(option))
    dispatch(saveCardio())
  }
  const setCapRefillOnChange = (option: IOption) => {
    dispatch(setCvCapRefill(option))
    dispatch(saveCardio())
  }
  const setEdemaChange = (option: IOption) => {
    dispatch(setCvEdema(option))
    dispatch(saveCardio())
  }

  const setTemperatureOnChange = (numbers: string[]) => {
    const newTemp = numbers.length ? numbers.join('') : null
    dispatch(setCvTemp(newTemp))
    dispatch(saveCardio())
  }

  const setTemparatureTypeOnChange = () => {
    const tempType = cvTempDegrees === t('cardio.F') ? t('cardio.C') : t('cardio.F')
    dispatch(setCvTempDegrees(tempType))
    dispatch(setCvTemp(cvTemp))
    dispatch(saveCardio())
  }

  useEffect(() => {
    if (cardioValidationFlag) {
      ma && dispatch(setMa(ma))
      rate && dispatch(setRate(rate))
      pmMode && dispatch(setPmMode(pmMode))
      cvTemp && dispatch(setCvTemp(cvTemp))
    }
  }, [cardioValidationFlag])

  useIonViewDidEnter(() => {
    setCardioValidationFlag(true)
  })

  useIonViewDidLeave(() => {
    setCardioValidationFlag(false)
  })

  const invasiveMonitoringData = (
    <>
      {showArtLine && (
        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              showChips={true}
              dataTestID='InvasiveMonitoring'
              listHeader={t('cardio.ArterialLine')}
              options={cvArtOptions}
              savedValues={cvArt}
              action={setCvArt}
              label={t('cardio.ArterialLine')}
              callback={saveCardio}
            />
          </IonCol>
        </IonRow>
      )}
      {showPaLine && (
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='PaLine'
              options={cvSwanOptions}
              selectedOption={cvSwan}
              listHeader={t('cardio.PaLine')}
              label={t('cardio.PaLine')}
              action={setPaLineOnChange}
            />
          </IonCol>
        </IonRow>
      )}
      <hr className='field-divider' />
    </>
  )

  return (
    <div id='cardioContent'>
      <IonList className={isTablet ? 'ion-margin-horizontal' : ''}>
        <IonRow>
          <IonCol className='ion-no-padding'>
            <AccordianComponent
              dataTestId={'pulses'}
              heading={t('cardio.pulses.pulses')}
              content={<PulsesContent />}
              isCheckMark={showPulses}
            />
          </IonCol>
        </IonRow>

        {(datapointsConfig.DP_HIDE_FIELDS_BY_STAFF === 0 || showPacemaker) && (
          <IonRow>
            <IonCol className='ion-no-padding'>
              <AccordianComponent
                dataTestId='Pacemaker'
                heading={t('cardio.Pacemaker')}
                content={<PacemakerContent />}
                isCheckMark={icPaceMakerCheck}
              />
            </IonCol>
          </IonRow>
        )}

        {(datapointsConfig.DP_HIDE_FIELDS_BY_STAFF === 0 || showInvasiveMonitoring) && (showPaLine || showArtLine) && (
          <IonRow>
            <IonCol className='ion-no-padding font-roboto'>
              <AccordianComponent
                dataTestId='InvasiveMonitoring'
                heading={t('cardio.InvasiveMonitoring')}
                content={invasiveMonitoringData}
                isCheckMark={invasiveMonitoringCheck}
              />
            </IonCol>
          </IonRow>
        )}
        {showTemp && (
          <>
            <IonRow>
              <IonCol size='6' className='picklist'>
                <NumberPad
                  className='cardio-numberpad'
                  elementId='Temperature'
                  dataTestId='Temperature'
                  values={cvTemp ? cvTemp.split('') : []}
                  label={t('cardio.Temperature')}
                  validCharacters={numberPadKeysWithDecimal}
                  onNumPadClick={setTemperatureOnChange}
                  action={CardioConstants.SET_CVTEMP}
                />
              </IonCol>

              <IonCol size='6' className='ion-no-padding'>
                <IonItem lines='none'>
                  <IonLabel className='toggle-prefix-width'>{t('cardio.C')}°</IonLabel>
                  <IonToggle
                    data-testid='TemperatureType'
                    className='temperature-ion-toggle'
                    checked={!cvTempDegrees || cvTempDegrees === t('cardio.F')}
                    onClick={setTemparatureTypeOnChange}
                  />
                  <IonLabel className='toggle-postfix-padding'>{t('cardio.F')}°</IonLabel>
                </IonItem>
              </IonCol>
            </IonRow>
            <Validation id={CardioConstants.SET_CVTEMP} />
            <hr className='field-divider' />
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='TempMethod'
                  options={cvTempMethodOptions}
                  selectedOption={cvTempMethod}
                  listHeader={t('cardio.Method')}
                  label={t('cardio.Method')}
                  action={setTempMethodOnChange}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='JVD'
              options={cvJvdOptions}
              selectedOption={cvJvd}
              listHeader={t('cardio.JVD')}
              label={t('cardio.JVD')}
              action={setJVDOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist' size='6'>
            <SingleSelect
              dataTestID='CapRefill'
              options={cvCapRefillOptions}
              selectedOption={cvCapRefill}
              listHeader={t('cardio.CapRefill')}
              label={t('cardio.CapRefill')}
              action={setCapRefillOnChange}
            />
          </IonCol>
          {showEdema && (
            <IonCol className='picklist' size='6'>
              <SingleSelect
                dataTestID='Edema'
                options={cvEdemaOptions}
                selectedOption={cvEdema}
                listHeader={t('cardio.Edema')}
                label={t('cardio.Edema')}
                action={setEdemaChange}
              />
            </IonCol>
          )}
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='ion-no-padding'>
            <Comment
              isDataPresent={cvComments.length > 0 ? true : false}
              path={TabConstants.INITIAL_ASSESSMENT_COMMENTS}
            />
          </IonCol>
        </IonRow>
        {prCvAssessOptions.length > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  showChips={true}
                  dataTestID='CardioExam'
                  listHeader={t('cardio.CardioExam')}
                  options={prCvAssessOptions}
                  savedValues={prCvAssess}
                  action={setPrCvAssess}
                  label={t('cardio.CardioExam')}
                  callback={saveCardio}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
      </IonList>
    </div>
  )
}

export default CardioContent
